import React, { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../constants/api';

const API = `${API_URL}/api`;
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@foundersplus.nl';

const ForgotPasswordForm = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [showDidntReceive, setShowDidntReceive] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendNotice, setResendNotice] = useState('');

  const submitForgotPassword = async (targetEmail) => {
    const res = await axios.post(`${API}/auth/forgot-password`, { email: targetEmail });
    const data = res.data;
    setEmailSent(!!data.email_sent);
    setEmailConfigured(data.email_configured !== false);
    return data;
  };

  const startCooldown = (seconds = 30) => {
    setResendCooldown(seconds);
    const tick = () => {
      setResendCooldown((s) => {
        if (s <= 1) return 0;
        setTimeout(tick, 1000);
        return s - 1;
      });
    };
    setTimeout(tick, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResetUrl('');
    setCopied(false);
    setShowDidntReceive(false);
    setResendNotice('');

    try {
      await submitForgotPassword(email);
      setSent(true);
      startCooldown(30);
    } catch (err) {
      let msg = 'Failed to process request';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      else if (typeof err.response?.data === 'string') msg = err.response.data;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError('');
    setResendNotice('');
    try {
      await submitForgotPassword(email);
      setResendNotice('We sent another link. Please give it a minute and check spam too.');
      startCooldown(30);
    } catch (err) {
      let msg = 'Could not resend. Please try again in a moment.';
      if (err.response?.data?.detail) msg = err.response.data.detail;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full space-y-6 sm:space-y-8 p-5 sm:p-8 card-calming" style={{ maxWidth: '28rem' }}>
        <div className="text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-primary-text heading-calming mb-2">Forgot Password</h2>
          <p className="text-calming">
            {emailConfigured ? "We'll send a reset link to your email" : 'Reset your password below'}
          </p>
        </div>

        {sent ? (
          <div className="space-y-4">
            {/* Case 1: Email sent */}
            {emailSent && (
              <>
                <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-4 rounded-lg text-center">
                  <p className="font-medium">Check your inbox!</p>
                  <p className="text-sm mt-1">
                    If that email is registered, a reset link has been sent.<br />
                    The link expires in <strong>1 hour</strong>.
                  </p>
                </div>

                <div className="text-center">
                  <button
                    onClick={() => setShowDidntReceive(!showDidntReceive)}
                    className="text-xs transition-colors hover:underline"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    Didn't receive it? {showDidntReceive ? '▲' : '▼'}
                  </button>
                </div>

                {showDidntReceive && (
                  <div className="rounded-lg p-4 space-y-3 text-sm" style={{
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: 'var(--text-muted, #888)',
                  }}>
                    <p className="font-medium" style={{ color: 'var(--text-primary, #eee)' }}>Troubleshooting:</p>
                    <ul className="space-y-1.5 text-xs">
                      <li>• Check your <strong>spam / junk</strong> folder</li>
                      <li>• Make sure you entered the right email</li>
                      <li>• Wait a few minutes &mdash; delivery can be slow</li>
                      <li>• Click <strong>Resend link</strong> below (up to 2 per hour)</li>
                    </ul>

                    <button
                      type="button"
                      onClick={handleResend}
                      disabled={resendCooldown > 0 || loading}
                      className="w-full px-3 py-2 rounded-lg text-sm font-medium transition-opacity"
                      style={{
                        backgroundColor: 'var(--accent-primary, #2dd4bf)',
                        color: '#0a0f14',
                        opacity: (resendCooldown > 0 || loading) ? 0.5 : 1,
                        cursor: (resendCooldown > 0 || loading) ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {loading
                        ? 'Sending...'
                        : resendCooldown > 0
                          ? `Resend link (${resendCooldown}s)`
                          : 'Resend link'}
                    </button>

                    {resendNotice && (
                      <p className="text-xs" style={{ color: '#22c55e' }}>{resendNotice}</p>
                    )}
                    {error && (
                      <p className="text-xs" style={{ color: '#f87171' }}>{error}</p>
                    )}

                    <div className="pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Still stuck? Use <strong>Change Password</strong> inside Settings if you're already signed in,
                        or email <a
                          href={`mailto:${SUPPORT_EMAIL}?subject=Password%20reset%20help&body=I%20cannot%20receive%20the%20password%20reset%20email%20for%3A%20${encodeURIComponent(email)}`}
                          style={{ color: 'var(--accent-primary, #2dd4bf)', textDecoration: 'underline' }}
                        >{SUPPORT_EMAIL}</a> and we'll reset it for you.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Case 2: send failed — same generic acknowledgement, never a link */}
            {!emailSent && (
              <>
                <div className="bg-green-900/50 border border-green-500 text-green-200 px-4 py-4 rounded-lg text-center">
                  <p className="font-medium">Request received</p>
                  <p className="text-sm mt-1">
                    If that email is registered, a reset link has been sent.<br />
                    The link expires in <strong>1 hour</strong>.
                  </p>
                </div>
                <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                  Didn't receive it? Check your spam folder or try again in a few minutes.
                </p>
              </>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            <div>
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full input-calming"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-calming-primary"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="text-center">
          <button
            onClick={onBack}
            className="text-calming hover:text-gentle-gold text-sm transition-colors"
          >
            Back to Sign In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
